export const NO_SIGNATURE_DATA_URL = 'data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22140%22%20height%3D%2240%22%20viewBox%3D%220%200%20140%2040%22%3E%3Ctext%20x%3D%2270%22%20y%3D%2224%22%20fill%3D%22%23555555%22%20font-family%3D%22Arial%2C%20Helvetica%2C%20sans-serif%22%20font-size%3D%2213%22%20text-anchor%3D%22middle%22%3ENo%20Signature%3C%2Ftext%3E%3C%2Fsvg%3E';
export const NO_THUMB_DATA_URL = 'data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2280%22%20height%3D%2280%22%20viewBox%3D%220%200%2080%2080%22%3E%3Ctext%20x%3D%2240%22%20y%3D%2244%22%20fill%3D%22%23555555%22%20font-family%3D%22Arial%2C%20Helvetica%2C%20sans-serif%22%20font-size%3D%2213%22%20text-anchor%3D%22middle%22%3ENo%20Thumb%3C%2Ftext%3E%3C%2Fsvg%3E';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import html2pdf from 'html2pdf.js';
import CommonModal from './Modal';
import { t } from '../utils/i18n';
import { getScopedTemplateCss, ID_CARD_SCOPE_CLASS } from '../utils/scopeCss';

// Front/Back Card Preview Component
const CardPreview = ({ assignment, template }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [barcodeDataUrl, setBarcodeDataUrl] = useState('');
  const cardRef = useRef(null);

  const payload = assignment?.render_payload || {};
  const ap = assignment?.army_personnel || {};
  const armyId = payload.army_id || ap.user?.army_id || ap.army_id || 'N/A';
  const fullName = payload.full_name || ap.full_name || 'N/A';
  const rankName = payload.rank || ap.rank || 'N/A';
  const subDeptName = payload.sub_department || ap.sub_department?.name || 'N/A';
  const forceName = payload.army_type || ap.army_type?.name || payload.organization || 'Puntland Armed Forces';
  const bloodTypeStr = payload.blood_type || ap.blood_type || 'N/A';
  const issueDateStr = payload.issue_date || (assignment?.issue_date ? new Date(assignment.issue_date).toLocaleDateString() : 'N/A');
  const expiryDateStr = payload.expiry_date || (assignment?.expiry_date ? new Date(assignment.expiry_date).toLocaleDateString() : 'N/A');

  useEffect(() => {
    if (armyId && armyId !== 'N/A') {
      // Dynamic QR Code payload with complete personnel information
      const qrPayload = [
        `PUNTLAND STATE - ${forceName.toUpperCase()}`,
        `ID: ${armyId}`,
        `Name: ${fullName}`,
        `Rank: ${rankName}`,
        `Department: ${subDeptName}`,
        `Blood Type: ${bloodTypeStr}`,
        `Issue Date: ${issueDateStr}`,
        `Expiry Date: ${expiryDateStr}`
      ].join('\n');

      QRCode.toDataURL(qrPayload, { margin: 1, width: 160 })
        .then(url => setQrCodeDataUrl(url))
        .catch(err => console.error('Error generating QR code:', err));

      // Dynamic Barcode generation using CODE128 for armyId (Transparent background)
      try {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, armyId, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true,
          fontSize: 12,
          margin: 2,
          background: 'rgba(0,0,0,0)',
          lineColor: '#000000'
        });
        setBarcodeDataUrl(canvas.toDataURL('image/png'));
      } catch (err) {
        console.error('Error generating barcode:', err);
      }
    }
  }, [armyId, fullName, rankName, forceName, subDeptName, bloodTypeStr, issueDateStr, expiryDateStr]);

  if (!assignment || !template) {
    return <div className="text-muted">No card data</div>;
  }

  const getLogoUrl = () => {
    const forceName = ap.army_type?.name || '';
    if (/police/i.test(forceName)) {
      return '/images/Police_army.jpeg';
    } else if (/darawiish|military/i.test(forceName)) {
      return '/images/Darawish_army.jpg';
    } else if (/custodial|correction|asluubta/i.test(forceName)) {
      return '/images/Custodial_army.jpg';
    }
    return '/images/logo.png';
  };

  const getBiometricStatus = () => {
    if (payload.biometric_status) {
      return payload.biometric_status;
    }
    const biometrics = ap.biometric_data || [];
    if (biometrics.length > 0) {
      return biometrics.map(b => {
        const type = b.biometric_type || '';
        return type.charAt(0).toUpperCase() + type.slice(1);
      }).join(', ');
    }
    return 'Not Enrolled';
  };

  const getPhotoUrl = () => {
    if (payload.photo_url) return payload.photo_url;
    const raw = ap.army_photo;
    if (raw && typeof raw === 'string' && raw.trim().length > 0) {
      const cleaned = raw.trim();
      if (cleaned.startsWith('data:') || cleaned.startsWith('http') || (cleaned.startsWith('/') && !cleaned.startsWith('/9j/'))) return cleaned;
      if (cleaned.startsWith('iVBOR')) return `data:image/png;base64,${cleaned}`;
      if (cleaned.startsWith('Qk')) return `data:image/bmp;base64,${cleaned}`;
      return `data:image/jpeg;base64,${cleaned}`;
    }
    const photoBio = (ap.biometric_data || []).find(b => b.biometric_type === 'photo' || b.biometric_type === 2 || b.biometric_type === '2');
    if (photoBio?.data) {
      const cleaned = photoBio.data.trim();
      if (cleaned.startsWith('data:') || cleaned.startsWith('http') || (cleaned.startsWith('/') && !cleaned.startsWith('/9j/'))) return cleaned;
      if (cleaned.startsWith('iVBOR')) return `data:image/png;base64,${cleaned}`;
      if (cleaned.startsWith('Qk')) return `data:image/bmp;base64,${cleaned}`;
      return `data:image/jpeg;base64,${cleaned}`;
    }
    return '/images/solider.png';
  };

  const getSignatureUrl = () => {
    if (payload.signature_url) return payload.signature_url;
    if (payload.holder_signature) return payload.holder_signature;
    if (payload.signature) return payload.signature;
    if (ap.signature_url) return ap.signature_url;
    if (ap.biometric_signature) return ap.biometric_signature;
    const sigBio = (ap.biometric_data || []).find(b => b.biometric_type === 'signature' || b.biometric_type === 1 || b.biometric_type === '1');
    if (sigBio?.data || sigBio?.raw_template) {
      const cleaned = (sigBio.data || sigBio.raw_template).trim();
      if (cleaned.startsWith('data:') || cleaned.startsWith('http') || cleaned.startsWith('/')) return cleaned;
      return `data:image/png;base64,${cleaned}`;
    }
    return NO_SIGNATURE_DATA_URL;
  };

  const getThumbprintUrl = () => {
    if (payload.thumbprint_url) return payload.thumbprint_url;
    const fpBio = (ap.biometric_data || []).find(b => b.biometric_type === 'fingerprint' || b.biometric_type === 0 || b.biometric_type === '0');
    const rawFp = fpBio?.data || fpBio?.raw_template;
    if (rawFp && typeof rawFp === 'string' && rawFp.trim().length > 0) {
      const cleaned = rawFp.trim();
      if (cleaned.startsWith('data:') || cleaned.startsWith('http') || (cleaned.startsWith('/') && !cleaned.startsWith('/9j/') && !cleaned.startsWith('iVBOR'))) return cleaned;
      if (cleaned.startsWith('iVBOR')) return `data:image/png;base64,${cleaned}`;
      if (cleaned.startsWith('Qk')) return `data:image/bmp;base64,${cleaned}`;
      if (cleaned.startsWith('/9j/')) return `data:image/jpeg;base64,${cleaned}`;
      return `data:image/png;base64,${cleaned}`;
    }
    return NO_THUMB_DATA_URL;
  };

  const data = {
    army_id: armyId,
    id: armyId,
    barcode_data: armyId,
    full_name: fullName,
    name: fullName,
    date_of_birth: payload.date_of_birth || (ap.birth_date ? new Date(ap.birth_date).toLocaleDateString() : 'N/A'),
    dob: payload.date_of_birth || (ap.birth_date ? new Date(ap.birth_date).toLocaleDateString() : 'N/A'),
    place_of_birth: payload.place_of_birth || ap.place_of_birth || 'N/A',
    pob: payload.place_of_birth || ap.place_of_birth || 'N/A',
    blood_type: bloodTypeStr,
    rank: rankName,
    title: payload.title || ap.title || rankName,
    sub_department: subDeptName,
    department: ap.department?.name || subDeptName,
    division: payload.division || subDeptName || ap.department?.name || 'N/A',
    organization_unit: payload.organization_unit || subDeptName || '1aad – Taliska Guud',
    organization: forceName,
    issue_date: issueDateStr,
    expiry_date: expiryDateStr,
    serial_number: payload.serial_number || (armyId !== 'N/A' ? armyId.replace(/[^0-9]/g, '') : '') || String(assignment?.id || '').padStart(6, '0'),
    photo_url: getPhotoUrl(),
    signature_url: getSignatureUrl(),
    holder_signature: getSignatureUrl(),
    signature: getSignatureUrl(),
    commander_signature_url: payload.commander_signature_url || (/police/i.test(forceName) ? "/images/sig.jpg" : "/images/pmfc-signature.jpg"),
    thumbprint_url: getThumbprintUrl(),
    logo_url: payload.logo_url || getLogoUrl(),
    qr_code: qrCodeDataUrl || '',
    barcode: barcodeDataUrl || '',
    army_type: forceName,
    biometric_status: getBiometricStatus(),
    return_notice: payload.return_notice || 'Haddii kaarkan la helo, fadlan u celi xafiiska dawladda ama xerada ciidanka ee ugu dhow.',
    border_text: payload.border_text || (/police/i.test(forceName) ? 'PUNTLAND POLICE FORCE • CIIDANKA BOOLISKA PUNTLAND' : /darawiish|military/i.test(forceName) ? 'PUNTLAND DARAWIISH FORCE • CIIDANKA DARAWIISHTA PUNTLAND' : 'PUNTLAND CUSTODIAL CORPS • CIIDANKA ASLUUBTA PUNTLAND'),
    contact_email: payload.contact_email || (/police/i.test(forceName) ? 'info@police.pl.so' : /darawiish|military/i.test(forceName) ? 'info@darawiish.pl.so' : 'info@asluubta.pl.so'),
    hotline: payload.hotline || (/police/i.test(forceName) ? '999' : '888'),
    coat_of_arms_url: payload.coat_of_arms_url || '/images/pmfc-coat-of-arms.png',
  };

  const replacePlaceholders = (html) => {
    let output = html || '';
    output = output.replace(/(src|href)=["\x27]\.\/images\//gi, "$1=\"/images/");
    // Automatically replace hardcoded thumbnail images with dynamic thumbprint_url
    output = output.replace(/(<img[^>]*src=["\x27])(\/images\/th\.jpg|\.\/images\/th\.jpg)(["\x27][^>]*>)/gi, `$1${data.thumbprint_url}$3`);
    Object.entries(data).forEach(([key, value]) => {
      const re = new RegExp(`{{\s*${key}\s*}}`, 'gi');
      output = output.replace(re, value == null ? '' : value);
    });
    // Fallback safety for any remaining image tags
    output = output.replace(/{{\s*signature_url\s*}}/gi, data.signature_url || NO_SIGNATURE_DATA_URL);
    output = output.replace(/{{\s*thumbprint_url\s*}}/gi, data.thumbprint_url || NO_THUMB_DATA_URL);
    output = output.replace(/{{\s*commander_signature_url\s*}}/gi, data.commander_signature_url || "/images/pmfc-signature.jpg");
    output = output.replace(/{{\s*photo_url\s*}}/gi, data.photo_url || "/images/solider.png");
    return output;
  };

const scopedCss = useMemo(() => {
  return getScopedTemplateCss(template.css_styles || "");
}, [template.css_styles]);

const frontHTML = replacePlaceholders(template.template_data);
  const backHTML = replacePlaceholders(template.back_template_data || `
    <div class="id-card-back">
      <div class="card-bg-overlay"></div>
      <div class="back-header">
        <img src="{{logo_url}}" class="back-logo" />
        <h2 class="org-name-back">{{organization}}</h2>
      </div>
      <div class="back-body">
        <div class="back-info-box">
          <div class="back-info-row back-row-one">
            <label>FORCE TYPE / NOOCA CIIDANKA:</label>
            <span class="back-val">{{army_type}}</span>
          </div>
          <div class="back-info-row back-row-two">
            <label>BIOMETRIC ENROLLMENT STATUS:</label>
            <span class="biometric-tag">{{biometric_status}}</span>
          </div>
        </div>
        <div class="back-notice">
          <h3>DIGNIIN / WARNING</h3>
          <p>{{return_notice}}</p>
          <p class="notice-en">If this ID card is found, please return it to the nearest Government Office or Military Command.</p>
        </div>
        <div class="back-signature-row" style="margin-top: 10px; display: flex; align-items: center; justify-content: space-between; border-top: 1px dashed #ccc; padding-top: 6px;">
          <span style="font-size: 10px; font-weight: bold; color: #444;">SAXIIXA / SIGNATURE:</span>
          <img src="{{signature_url}}" class="card-signature" alt="Signature" style="max-height: 28px; max-width: 100px; object-fit: contain;" onerror="this.style.display='none'" />
        </div>
      </div>
      <div class="back-footer">
        <div class="footer-seal-back">CONFIDENTIAL & RESTRICTED</div>
      </div>
    </div>
  `);

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>ID Card</title>
          <style>
            ${scopedCss}
            body { margin: 0; padding: 0; }
            .print-container { display: flex; flex-direction: row; gap: 10mm; justify-content: center; align-items: center; padding: 20px; flex-wrap: wrap; }
            .card-side { page-break-inside: avoid; text-align: left; }
          </style>
        </head>
        <body>
          <div class="print-container ${ID_CARD_SCOPE_CLASS}">
            <div class="card-side">
              ${frontHTML}
            </div>
            <div class="card-side">
              ${backHTML}
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    if (!html2pdf) {
      alert('PDF library not loaded. Please refresh the page.');
      return;
    }
    const element = cardRef.current;
    const opt = {
      margin: 10,
      filename: `${assignment?.army_personnel?.full_name || 'ID_Card'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' }
    };
    html2pdf().set(opt).from(element).save();
  };

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <button className="btn btn-success" onClick={handlePrint}>
          Print
        </button>
        <button className="btn btn-info" onClick={handleDownloadPDF}>
          Download PDF
        </button>
      </div>

      <div
        ref={cardRef}
        className={ID_CARD_SCOPE_CLASS}
        style={{
          padding: '20px',
          backgroundColor: '#fff',
          textAlign: 'center',
          width: '100%',
          display: 'block'
        }}
      >
        <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
        <style dangerouslySetInnerHTML={{
          __html: `
          .id-card-front, .id-card-back {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            break-after: avoid !important;
            break-inside: avoid !important;
          }
        ` }} />
        <div style={{ display: 'inline-block', verticalAlign: 'top', margin: '10px', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', background: '#fff', textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: frontHTML }} />
        <div style={{ display: 'inline-block', verticalAlign: 'top', margin: '10px', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', background: '#fff', textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: backHTML }} />
      </div>
    </div>
  );
};

const IdCardManagement = ({ onBack, api }) => {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [armyTypes, setArmyTypes] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assignments');
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formType, setFormType] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [previewAssignment, setPreviewAssignment] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [personnelFilters, setPersonnelFilters] = useState({
    armyId: '',
    name: '',
    armyTypeId: '',
    departmentId: '',
    subDepartmentId: '',
    rank: '',
    organization: ''
  });

  const visibleDepts = useMemo(() => {
    let depts = personnelFilters.armyTypeId
      ? departments.filter(d => String(d.army_type_id) === String(personnelFilters.armyTypeId))
      : departments;
    const seen = new Set();
    return depts.filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });
  }, [personnelFilters.armyTypeId, departments]);

  const visibleSubDepts = useMemo(() => {
    let subDepts = subDepartments;
    if (personnelFilters.departmentId) {
      subDepts = subDepts.filter(sd => String(sd.parent_id) === String(personnelFilters.departmentId));
    } else if (personnelFilters.armyTypeId) {
      const parentDeptIds = departments
        .filter(d => String(d.army_type_id) === String(personnelFilters.armyTypeId))
        .map(d => d.id);
      subDepts = subDepts.filter(sd => parentDeptIds.includes(sd.parent_id));
    }
    const seen = new Set();
    return subDepts.filter(sd => {
      if (seen.has(sd.id)) return false;
      seen.add(sd.id);
      return true;
    });
  }, [personnelFilters.departmentId, personnelFilters.armyTypeId, subDepartments, departments]);

  const defaultFrontHTML = `<div class="id-card-front">
    <div class="front-header">
      <img src="{{logo_url}}" class="logo" />
      <h2>{{organization}}</h2>
    </div>
    <div class="front-content">
      <div class="photo-section">
        <img src="{{photo_url}}" class="photo" />
      </div>
      <div class="details-section">
        <div class="detail-row">
          <label>ARMY ID / TIRADA CIIDANKA</label>
          <span>{{army_id}}</span>
        </div>
        <div class="detail-row">
          <label>NAME / MAGACA</label>
          <span>{{full_name}}</span>
        </div>
        <div class="detail-row">
          <label>DATE OF BIRTH / MAALINTA DHALATADA</label>
          <span>{{date_of_birth}}</span>
        </div>
        <div class="detail-row">
          <label>PLACE OF BIRTH / MESHA DHALATADA</label>
          <span>{{place_of_birth}}</span>
        </div>
        <div class="detail-row">
          <label>BLOOD TYPE / NOOCA DHIIGA</label>
          <span>{{blood_type}}</span>
        </div>
        <div class="detail-row">
          <label>RANK / DARAJADA</label>
          <span>{{rank}}</span>
        </div>
        <div class="detail-row">
          <label>SUB-DEPARTMENT / WAAXDA YAR</label>
          <span>{{sub_department}}</span>
        </div>
        <div class="detail-row">
          <label>ISSUE DATE / MAALINTA SIDA</label>
          <span>{{issue_date}}</span>
        </div>
        <div class="detail-row">
          <label>EXPIRY DATE / MAALINTA DHAMAADKA</label>
          <span>{{expiry_date}}</span>
        </div>
      </div>
    </div>
  </div>`;

  const defaultCSS = `.id-card-front, .id-card-back {
    width: 350px;
    height: 550px;
    border: 2px solid #333;
    border-radius: 8px;
    padding: 15px;
    font-family: Arial, sans-serif;
    background: linear-gradient(135deg, #f5f5f5 0%, #fff 100%);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }
  .front-header, .back-header {
    text-align: center;
    border-bottom: 2px solid #333;
    padding-bottom: 10px;
    margin-bottom: 10px;
  }
  .logo, .back-logo {
    height: 40px;
    margin-bottom: 5px;
  }
  .front-header h2, .back-header h3 {
    margin: 5px 0;
    font-size: 14px;
  }
  .photo-section {
    text-align: center;
    margin-bottom: 10px;
  }
  .photo {
    width: 80px;
    height: 100px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  .details-section {
    font-size: 11px;
  }
  .detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    border-bottom: 1px dotted #ccc;
    padding-bottom: 3px;
  }
  .detail-row label {
    font-weight: bold;
    flex: 1;
  }
  .detail-row span {
    flex: 1;
    text-align: right;
  }
  .back-content {
    padding: 20px 0;
  }
  .back-field {
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px dotted #ccc;
  }
  .back-field label {
    font-weight: bold;
    display: block;
    margin-bottom: 5px;
  }
  .back-notice {
    margin-top: 20px;
    padding: 10px;
    background: #fffacd;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 10px;
    text-align: center;
    font-style: italic;
  }`;

  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    army_type_id: '',
    status: 'active',
    template_data: defaultFrontHTML,
    back_template_data: '',
    css_styles: defaultCSS,
  });

  const [formData, setFormData] = useState({
    army_personnel_id: '',
    id_card_template_id: '',
    issue_date: '',
    expiry_date: '',
    status: 'pending',
  });

  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePersonnelChange = (e) => {
    const selectedId = e.target.value;
    const selectedPerson = personnel.find(p => String(p.id) === String(selectedId));
    let matchedTemplateId = formData.id_card_template_id;

    if (selectedPerson && selectedPerson.army_type_id) {
      const matchingTemplate = templates.find(t => String(t.army_type_id) === String(selectedPerson.army_type_id));
      if (matchingTemplate) {
        matchedTemplateId = matchingTemplate.id;
      }
    }

    setFormData(prev => ({
      ...prev,
      army_personnel_id: selectedId,
      id_card_template_id: matchedTemplateId
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

const loadData = async () => {
  setLoading(true);
  try {
    // 1. Instantly fetch what is needed for displaying the ID card assignments & templates tables
    const [assignmentsRes, templatesRes, armyTypesRes] = await Promise.all([
      api.get("/id_card_assignments"),
      api.get("/id_card_templates"),
      api.get("/army_types"),
    ]);
    setAssignments(assignmentsRes.data.data || []);
    setTemplates(templatesRes.data.data || []);
    setArmyTypes(Array.isArray(armyTypesRes) ? armyTypesRes : (armyTypesRes?.data || []));
  } catch (error) {
    console.error("Error loading ID card data:", error);
  } finally {
    setLoading(false);
  }

  // 2. Fetch dropdown options (compact personnel without photos, departments, ranks) in the background so modal is instant
  loadPersonnelForAssign();
};

const loadPersonnelForAssign = async () => {
  try {
    const [personnelRes, deptsRes, ranksRes] = await Promise.all([
      api.get("/army_personnel?compact=true&exclude_photo=true"),
      api.get("/departments"),
      api.get("/ranks"),
    ]);
    setPersonnel(personnelRes.data.army_personnel || []);
    const allDepts = deptsRes.data.departments || deptsRes.data.data || deptsRes.data || [];
    setDepartments(allDepts.filter(d => !d.parent_id));
    setSubDepartments(allDepts.filter(d => d.parent_id));
    setRanks(ranksRes.data.ranks || ranksRes.data.data || ranksRes.data || []);
  } catch (err) {
    console.warn("Background load of personnel options completed with warning:", err);
  }
};

  const handleEditAssignment = (assignment) => {
    setFormType('edit-assignment');
    setEditingId(assignment.id);
    setFormData({
      army_personnel_id: assignment.army_personnel_id,
      id_card_template_id: assignment.id_card_template_id,
      issue_date: assignment.issue_date || '',
      expiry_date: assignment.expiry_date || '',
      status: assignment.status || 'pending',
    });
    setShowForm(true);
  };

const handlePreviewCard = async (assignment) => {
  let fullAssignment = assignment;
  // If full card payload (like photo, signature, barcode, template_data) isn't loaded yet, fetch it on-demand in <50ms!
  if (!assignment.render_payload || !assignment.id_card_template?.template_data) {
    try {
      const res = await api.get(`/id_card_assignments/${assignment.id}`);
      const unwrapped = res?.data?.data || res?.data || res;
      if (unwrapped && (unwrapped.render_payload || unwrapped.id)) {
        fullAssignment = unwrapped;
      }
    } catch (err) {
      console.error('Failed to load card details for preview', err);
    }
  }
  const template = templates.find(t => t.id === fullAssignment.id_card_template_id) || fullAssignment.id_card_template;
  setPreviewAssignment(fullAssignment);
  setPreviewTemplate(template);
  setShowPreview(true);
};

const handlePreviewTemplate = (template) => {
  // 1. Try to find an existing assignment that uses this template
  const existingAssignment = assignments.find(a => a.id_card_template_id === template.id);
  if (existingAssignment) {
    setPreviewAssignment(existingAssignment);
    setPreviewTemplate(template);
    setShowPreview(true);
    return;
  }

  // 2. Otherwise create a sample mock assignment for previewing this template
  const matchingPersonnel = personnel.find(p => String(p.army_type_id) === String(template.army_type_id)) || personnel[0] || {};
  const force = armyTypes.find(at => String(at.id) === String(template.army_type_id)) || matchingPersonnel.army_type || { name: "Puntland Armed Forces" };
  const forceName = force.name || "Puntland Armed Forces";

  const mockAssignment = {
    id: 9999,
    army_personnel: {
      id: matchingPersonnel.id || 1,
      full_name: matchingPersonnel.full_name || "Ahmed Mohamed Ali",
      army_id: matchingPersonnel.army_id || matchingPersonnel.user?.army_id || "CA-001234",
      rank: matchingPersonnel.rank || "Dhame",
      blood_type: matchingPersonnel.blood_type || "O+",
      birth_date: matchingPersonnel.birth_date || "1995-05-12",
      place_of_birth: matchingPersonnel.place_of_birth || "Garowe",
      organization: matchingPersonnel.organization || forceName,
      army_type: force,
      army_photo: matchingPersonnel.army_photo || "/images/solider.png",
      sub_department: matchingPersonnel.sub_department || { name: "General Command" },
      department: matchingPersonnel.department || { name: "Operations" },
      biometric_data: matchingPersonnel.biometric_data || [
        { biometric_type: "signature", data: NO_SIGNATURE_DATA_URL },
        { biometric_type: "fingerprint", data: NO_THUMB_DATA_URL }
      ]
    },
    issue_date: new Date().toISOString().split("T")[0],
    expiry_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    id_card_template: template,
    id_card_template_id: template.id
  };

  setPreviewAssignment(mockAssignment);
  setPreviewTemplate(template);
  setShowPreview(true);
};

const handlePreviewFormTemplate = () => {
  const tempTemplate = {
    id: editingId || 0,
    name: templateFormData.name || "Template Preview",
    army_type_id: templateFormData.army_type_id ? parseInt(templateFormData.army_type_id) : null,
    army_type: armyTypes.find(at => String(at.id) === String(templateFormData.army_type_id)),
    template_data: templateFormData.template_data,
    back_template_data: templateFormData.back_template_data,
    css_styles: templateFormData.css_styles,
    status: templateFormData.status || "active"
  };
  handlePreviewTemplate(tempTemplate);
};

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await api.patch(`/id_card_assignments/${editingId}`, { id_card_assignment: formData });
      } else {
        await api.post('/id_card_assignments', { id_card_assignment: formData });
      }
      loadData();
      setShowForm(false);
      setFormData({ army_personnel_id: '', id_card_template_id: '', issue_date: '', expiry_date: '', status: 'pending' });
      setPersonnelSearch('');
      setPersonnelFilters({
        armyId: '',
        name: '',
        armyTypeId: '',
        departmentId: '',
        subDepartmentId: '',
        rank: '',
        organization: ''
      });
    } catch (error) {
      console.error('Error saving assignment:', error);
      alert('Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await api.patch(`/id_card_templates/${editingId}`, { id_card_template: templateFormData });
      } else {
        await api.post('/id_card_templates', { id_card_template: templateFormData });
      }
      loadData();
      setShowForm(false);
      setTemplateFormData({
        name: '',
        army_type_id: '',
        status: 'active',
        template_data: defaultFrontHTML,
        back_template_data: '',
        css_styles: defaultCSS,
      });
      setEditingId(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ army_personnel_id: '', id_card_template_id: '', issue_date: '', expiry_date: '', status: 'pending' });
    setTemplateFormData({
      name: '',
      army_type_id: '',
      status: 'active',
      template_data: defaultFrontHTML,
      back_template_data: '',
      css_styles: defaultCSS,
    });
    setEditingId(null);
    setPersonnelSearch('');
    setPersonnelFilters({
      armyId: '',
      name: '',
      armyTypeId: '',
      departmentId: '',
      subDepartmentId: '',
      rank: '',
      organization: ''
    });
  };

  const handleCreateTemplate = () => {
    setFormType('create-template');
    setEditingId(null);
    setShowForm(true);
  };

  const handleEditTemplate = (template) => {
    setFormType('edit-template');
    setEditingId(template.id);
    setTemplateFormData({
      name: template.name,
      army_type_id: template.army_type_id || '',
      status: template.status || 'active',
      template_data: template.template_data,
      back_template_data: template.back_template_data || '',
      css_styles: template.css_styles,
    });
    setShowForm(true);
  };

  const handleDeleteTemplate = async (id) => {
    if (confirm('Delete this template?')) {
      try {
        await api.delete(`/id_card_templates/${id}`);
        loadData();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const handleIssueCard = async (id) => {
    if (confirm('Issue this card?')) {
      try {
        await api.patch(`/id_card_assignments/${id}/issue`);
        loadData();
      } catch (error) {
        console.error('Error issuing card:', error);
      }
    }
  };

  const handleRevokeCard = async (id) => {
    if (confirm('Revoke this card?')) {
      try {
        await api.patch(`/id_card_assignments/${id}/revoke`);
        loadData();
      } catch (error) {
        console.error('Error revoking card:', error);
      }
    }
  };

  const filteredAssignments = assignments.filter(a => {
    if (filters.status && a.status !== filters.status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!a.army_personnel?.full_name?.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const availablePersonnel = personnel.filter(p => {
    const hasAssignment = assignments.some(a => a.army_personnel_id === p.id);
    const isCurrentlyEditing = editingId && assignments.find(a => a.id === editingId)?.army_personnel_id === p.id;
    if (hasAssignment && !isCurrentlyEditing) return false;

    if (personnelFilters.armyId && !String(p.army_id || p.user?.army_id || '').toLowerCase().includes(personnelFilters.armyId.toLowerCase())) return false;
    if (personnelFilters.name && !String(p.full_name || '').toLowerCase().includes(personnelFilters.name.toLowerCase())) return false;
    if (personnelFilters.organization && !String(p.organization || '').toLowerCase().includes(personnelFilters.organization.toLowerCase())) return false;
    if (personnelFilters.armyTypeId && String(p.army_type_id) !== String(personnelFilters.armyTypeId)) return false;
    if (personnelFilters.departmentId && String(p.department_id) !== String(personnelFilters.departmentId)) return false;
    if (personnelFilters.subDepartmentId && String(p.sub_department_id) !== String(personnelFilters.subDepartmentId)) return false;
    if (personnelFilters.rank && String(p.rank || '') !== String(personnelFilters.rank)) return false;

    return true;
  });

  if (loading) return <div className="text-center p-4">Loading...</div>;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'assignments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('assignments')}
                  >
                    Card Assignments
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
                    onClick={() => setActiveTab('templates')}
                  >
                    Templates
                  </button>
                </li>
              </ul>

              {activeTab === 'assignments' && (
                <div>
                  <div className="mb-3">
                    <button className="btn btn-primary" onClick={() => { setFormType('create-assignment'); setShowForm(true); }}>
                      Assign New Card
                    </button>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <select className="form-control" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="issued">Issued</option>
                        <option value="revoked">Revoked</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input type="text" className="form-control" placeholder="Search..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
                    </div>
                  </div>
                  <div className="baseTableContainer">
                    <table className="baseTable">
                      <thead>
                        <tr>
                          <th>Personnel</th>
                          <th>Army ID</th>
                          <th>Template</th>
                          <th>Status</th>
                          <th>Issue Date</th>
                          <th>Expiry Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAssignments.map(a => (
                          <tr key={a.id}>
                            <td>{a.army_personnel?.full_name}</td>
                            <td>{a.army_personnel?.army_id || a.army_personnel?.user?.army_id || 'N/A'}</td>
                            <td>{a.id_card_template?.name}</td>
                            <td><span className={`badge badge-${a.status === 'issued' ? 'success' : 'warning'}`}>{a.status}</span></td>
                            <td>{a.issue_date || '-'}</td>
                            <td>{a.expiry_date || '-'}</td>
                            <td>
                              <button className="btn btn-sm btn-info" onClick={() => handlePreviewCard(a)}>Preview</button>
                              <button className="btn btn-sm btn-secondary ms-1" onClick={() => handleEditAssignment(a)}>Edit</button>
                              {a.status === 'pending' && <button className="btn btn-sm btn-success ms-1" onClick={() => handleIssueCard(a.id)}>Issue</button>}
                              {a.status === 'issued' && <button className="btn btn-sm btn-danger ms-1" onClick={() => handleRevokeCard(a.id)}>Revoke</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'templates' && (
                <div>
                  <div className="mb-3">
                    <button className="btn btn-primary" onClick={handleCreateTemplate}>Create Template</button>
                  </div>
                  <div className="baseTableContainer">
                    <table className="baseTable">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Army Type</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map(t => (
                          <tr key={t.id}>
                            <td>{t.name}</td>
                            <td>{t.army_type?.name || 'All'}</td>
                            <td><span className={`badge badge-${t.status === 'active' ? 'success' : 'secondary'}`}>{t.status}</span></td>
                            <td>
                              <button className="btn btn-sm btn-info" onClick={() => handlePreviewTemplate(t)}>Preview</button>
                              <button className="btn btn-sm btn-secondary ms-1" onClick={() => handleEditTemplate(t)}>Edit</button>
                              <button className="btn btn-sm btn-danger ms-1" onClick={() => handleDeleteTemplate(t.id)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <CommonModal isOpen={showPreview} onClose={() => setShowPreview(false)} title={previewTemplate ? `ID Card Preview - ${previewTemplate.name}` : "ID Card Preview"} size="lg" zIndex={1070}>
        {previewAssignment && previewTemplate && <CardPreview assignment={previewAssignment} template={previewTemplate} />}
      </CommonModal>

      {/* Form Modal */}
      <CommonModal isOpen={showForm} onClose={handleCancel} title={formType === 'create-template' ? 'Create Template' : formType === 'edit-template' ? 'Edit Template' : formType === 'create-assignment' ? 'Assign Card' : 'Edit Assignment'} size="lg">
        {formType === 'create-template' || formType === 'edit-template' ? (
          <form onSubmit={handleTemplateSubmit}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Template Name *</label>
                <input type="text" className="form-control" value={templateFormData.name} onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })} required />
              </div>
              <div className="col-md-6">
                <label>Army Type</label>
                <select className="form-control" value={templateFormData.army_type_id} onChange={(e) => setTemplateFormData({ ...templateFormData, army_type_id: e.target.value })}>
                  <option value="">All Forces</option>
                  {armyTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label>Front Side HTML *</label>
              <textarea className="form-control" rows="8" value={templateFormData.template_data} onChange={(e) => setTemplateFormData({ ...templateFormData, template_data: e.target.value })} style={{ fontFamily: 'monospace', fontSize: '12px' }} required />
            </div>
            <div className="mb-3">
              <label>Back Side HTML</label>
              <textarea className="form-control" rows="8" value={templateFormData.back_template_data} onChange={(e) => setTemplateFormData({ ...templateFormData, back_template_data: e.target.value })} style={{ fontFamily: 'monospace', fontSize: '12px' }} />
            </div>
            <div className="mb-3">
              <label>CSS Styles *</label>
              <textarea className="form-control" rows="8" value={templateFormData.css_styles} onChange={(e) => setTemplateFormData({ ...templateFormData, css_styles: e.target.value })} style={{ fontFamily: 'monospace', fontSize: '12px' }} required />
            </div>
            <div className="mb-3 p-2 bg-light border rounded" style={{ fontSize: '11px' }}>
              <div className="fw-bold mb-1 text-secondary">Available Template Placeholders:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {['{{army_id}}', '{{full_name}}', '{{rank}}', '{{sub_department}}', '{{organization}}', '{{army_type}}', '{{blood_type}}', '{{date_of_birth}}', '{{place_of_birth}}', '{{issue_date}}', '{{expiry_date}}', '{{photo_url}}', '{{signature_url}}', '{{commander_signature_url}}', '{{thumbprint_url}}', '{{logo_url}}', '{{qr_code}}', '{{barcode}}', '{{biometric_status}}', '{{return_notice}}'].map(tag => (
                  <code key={tag} style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '3px', color: '#1e293b' }}>{tag}</code>
                ))}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
              <button type="button" className="btn btn-info text-white" onClick={handlePreviewFormTemplate}>Preview Template</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleAssignmentSubmit}>
            <div className="row">
              {/* Personnel Selection with Filters */}
              <div className="col-md-7 border-end pe-3">
                <h6 className="fw-bold mb-3 text-primary">Filter & Select Personnel</h6>

                <div className="row g-2 mb-3">
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Army ID</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. CA-000001"
                      value={personnelFilters.armyId}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, armyId: e.target.value })}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search name..."
                      value={personnelFilters.name}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, name: e.target.value })}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Organization</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Unit A"
                      value={personnelFilters.organization}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, organization: e.target.value })}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Army Type / Force</label>
                    <select
                      className="form-select form-select-sm"
                      value={personnelFilters.armyTypeId}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, armyTypeId: e.target.value, departmentId: '', subDepartmentId: '' })}
                    >
                      <option value="">All Army Types</option>
                      {armyTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Department</label>
                    <select
                      className="form-select form-select-sm"
                      value={personnelFilters.departmentId}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, departmentId: e.target.value, subDepartmentId: '' })}
                    >
                      <option value="">All Departments</option>
                      {visibleDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Subdepartment</label>
                    <select
                      className="form-select form-select-sm"
                      value={personnelFilters.subDepartmentId}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, subDepartmentId: e.target.value })}
                    >
                      <option value="">All Subdepartments</option>
                      {visibleSubDepts.map(sd => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small mb-1 fw-semibold">Rank</label>
                    <select
                      className="form-select form-select-sm"
                      value={personnelFilters.rank}
                      onChange={(e) => setPersonnelFilters({ ...personnelFilters, rank: e.target.value })}
                    >
                      <option value="">All Ranks</option>
                      {ranks.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-3 pt-2 border-top">
                  <label className="form-label fw-bold mb-1">Select Personnel *</label>
                  <select
                    className="form-control"
                    value={formData.army_personnel_id}
                    onChange={handlePersonnelChange}
                    required
                    style={{ border: '2px solid #0d6efd', borderRadius: '6px' }}
                  >
                    <option value="">-- Choose Personnel ({availablePersonnel.length} matching) --</option>
                    {availablePersonnel.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.full_name} ({p.army_id || p.user?.army_id || 'No ID'}) - {p.rank || 'No Rank'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Template & Dates */}
              <div className="col-md-5 ps-3">
                <h6 className="fw-bold mb-3 text-secondary">Card Settings</h6>

                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Template *</label>
                  <select className="form-control" value={formData.id_card_template_id} onChange={(e) => setFormData({ ...formData, id_card_template_id: e.target.value })} required>
                    <option value="">Select Template</option>
                    {templates.length > 0 ? (
                      templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)
                    ) : (
                      <option disabled>No templates available</option>
                    )}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Issue Date</label>
                  <input name="issue_date" type="date" className="form-control" value={formData.issue_date} onChange={handleDateChange} />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Expiry Date</label>
                  <input name="expiry_date" type="date" className="form-control" value={formData.expiry_date} onChange={handleDateChange} />
                </div>

                <div className="pt-3 border-top text-end">
                  <button type="submit" className="btn btn-primary px-4 btn-lg w-100" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Assignment'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </CommonModal>
    </div>
  );
};

export default IdCardManagement;
